import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, Plus } from "lucide-react";
import ItemFormDialog from "@/components/ItemFormDialog";
import ImageDropZone from "./ImageDropZone";
import BulkUploadPanel from "./BulkUploadPanel";
import CategoriesPanel from "./CategoriesPanel";
import { ModuleHeader } from "./AdminUI";
import { SPECIAL_SECTIONS, errMsg, fieldCls } from "./adminShared";

const GRID = "grid grid-cols-[64px_1fr_110px] md:grid-cols-[64px_1fr_130px_100px_70px_80px_90px] gap-3 md:gap-4";

function MenuItemRow({ item, onSave, onEdit, onDelete }) {
    const [price, setPrice] = useState(item.price);
    useEffect(() => { setPrice(item.price); }, [item.price]);
    return (
        <div data-testid={`admin-item-row-${item.slug}`} className={`${GRID} px-4 py-3 items-center border-t border-brand-line text-sm ${item.sold_out ? "bg-red-500/[0.04]" : ""}`}>
            <ImageDropZone currentUrl={item.image_url} onUploaded={(url) => onSave(item.id, { image_url: url })} />
            <div className="min-w-0">
                <p className="text-brand-cream truncate">{item.name}</p>
                {item.description && <p className="text-[11px] text-brand-mute truncate">{item.description}</p>}
                {item.sold_out && <p className="text-[10px] uppercase tracking-wider text-red-300 mt-0.5">Sold out today</p>}
            </div>
            <p className="hidden md:block text-brand-mute text-xs truncate">{item.category}</p>
            <input
                data-testid={`admin-item-price-${item.slug}`}
                type="number" step="0.01" value={price}
                onChange={(e) => setPrice(e.target.value)}
                onBlur={() => Number(price) !== item.price && onSave(item.id, { price: Number(price) })}
                className="hidden md:block w-24 bg-brand-ink border border-brand-line px-2 py-1 rounded-sm"
            />
            <div className="hidden md:block">
                <Switch data-testid={`admin-item-avail-${item.slug}`} checked={item.available} onCheckedChange={(v) => onSave(item.id, { available: v })} />
            </div>
            <div className="hidden md:block">
                <Switch data-testid={`admin-item-soldout-${item.slug}`} checked={!!item.sold_out} onCheckedChange={(v) => onSave(item.id, { sold_out_today: v })} />
            </div>
            <div className="flex items-center gap-1.5 justify-end md:justify-start">
                <button data-testid={`admin-edit-${item.slug}`} onClick={onEdit} className="p-1.5 border border-brand-line hover:border-brand-olive hover:text-brand-gold rounded-sm" title="Edit"><Pencil size={13} /></button>
                <button data-testid={`admin-delete-${item.slug}`} onClick={onDelete} className="p-1.5 border border-brand-line hover:border-destructive hover:text-destructive rounded-sm" title="Delete"><Trash2 size={13} /></button>
            </div>
        </div>
    );
}

function ItemsPanel({ items, categories, reload }) {
    const [search, setSearch] = useState("");
    const [cat, setCat] = useState("all");
    const [dialog, setDialog] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const save = async (id, updates) => {
        try {
            await client.patch(`/admin/menu/${id}`, updates);
            toast.success("Saved");
            reload();
        } catch (err) {
            toast.error(errMsg(err, "Save failed"));
        }
    };

    const doDelete = async () => {
        await client.delete(`/admin/menu/${deleting.id}`);
        toast.success(`${deleting.name} deleted`);
        setDeleting(null);
        reload();
    };

    const q = search.trim().toLowerCase();
    const filtered = items
        .filter((it) => cat === "all" || it.category === cat)
        .filter((it) => !q || it.name.toLowerCase().includes(q) || (it.description || "").toLowerCase().includes(q));

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="flex flex-wrap items-center gap-2">
                    <input data-testid="admin-menu-search" placeholder="Search items…" value={search} onChange={(e) => setSearch(e.target.value)} className={`${fieldCls} !mt-0 w-64`} />
                    <select data-testid="admin-menu-cat-filter" value={cat} onChange={(e) => setCat(e.target.value)} className={`${fieldCls} !mt-0 w-auto`}>
                        <option value="all">All categories</option>
                        {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                    </select>
                    <span className="text-xs text-brand-mute">{filtered.length} of {items.length}</span>
                </div>
                <button data-testid="admin-add-item-btn" onClick={() => setDialog({ mode: "create" })} className="btn-primary inline-flex items-center gap-2 text-sm"><Plus size={14} /> Add item</button>
            </div>
            <div className="border border-brand-line rounded-sm overflow-hidden bg-brand-charcoal">
                <div className={`${GRID} px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-brand-mute bg-brand-ink`}>
                    <span>Image</span><span>Item</span><span className="hidden md:block">Category</span><span className="hidden md:block">Price (R)</span>
                    <span className="hidden md:block">Shown</span><span className="hidden md:block">Sold out</span><span className="text-right md:text-left">Actions</span>
                </div>
                {filtered.map((it) => (
                    <MenuItemRow key={it.id} item={it} onSave={save} onEdit={() => setDialog({ mode: "edit", item: it })} onDelete={() => setDeleting(it)} />
                ))}
                {filtered.length === 0 && <div className="p-8 text-center text-brand-mute text-sm">No items match your filter.</div>}
            </div>
            <ItemFormDialog
                open={!!dialog}
                onOpenChange={(o) => !o && setDialog(null)}
                mode={dialog?.mode || "create"}
                item={dialog?.item}
                categories={categories}
                onSaved={reload}
            />
            <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
                <AlertDialogContent className="bg-brand-charcoal border border-brand-line text-brand-cream rounded-sm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-2xl">Delete item?</AlertDialogTitle>
                        <AlertDialogDescription className="text-brand-mute">"{deleting?.name}" will be removed from the menu. Past orders are unaffected.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="btn-ghost">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid="admin-delete-confirm" onClick={doDelete} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-sm">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default function MenuModule() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const loadItems = () => client.get("/admin/menu").then((r) => setItems(r.data));
    const loadCats = () => client.get("/categories").then((r) => setCategories(r.data.filter((c) => !SPECIAL_SECTIONS.includes(c.slug))));
    useEffect(() => { loadItems(); loadCats(); }, []);

    return (
        <div data-testid="admin-menu-module">
            <ModuleHeader eyebrow="Catalogue" title="Menu" subtitle="Edit items, prices and photos. 'Sold out today' resets automatically at midnight; 'Shown' hides an item until you switch it back on." />
            <Tabs defaultValue="items">
                <TabsList className="bg-brand-charcoal border border-brand-line rounded-sm">
                    <TabsTrigger value="items" data-testid="menu-tab-items">Items</TabsTrigger>
                    <TabsTrigger value="categories" data-testid="menu-tab-categories">Categories</TabsTrigger>
                    <TabsTrigger value="images" data-testid="menu-tab-images">Bulk images</TabsTrigger>
                </TabsList>
                <TabsContent value="items" className="mt-6"><ItemsPanel items={items} categories={categories} reload={loadItems} /></TabsContent>
                <TabsContent value="categories" className="mt-6"><CategoriesPanel onChanged={loadCats} /></TabsContent>
                <TabsContent value="images" className="mt-6"><BulkUploadPanel onDone={loadItems} /></TabsContent>
            </Tabs>
        </div>
    );
}
